<script>
import FilterBar from './FilterBar.vue';
import StatusCountsWidget from './StatusCountsWidget.vue';
import TicketColumn from './TicketColumn.vue';
import { queueSortTickets } from '../queueSortTickets';

/** Column keys mirror the board payload groups (data.* and meta.columns.*). */
const DEFAULT_COLUMNS = [
    { key: 'new', label: 'Nuovo' },
    { key: 'assigned', label: 'Assegnato' },
    { key: 'in_progress', label: 'In lavorazione' },
    { key: 'blocked', label: 'Stand-by' },
    { key: 'closed', label: 'Chiusi' },
];

/**
 * Presentational board shell. Parent supplies tickets + filter state;
 * grouping maps status `merged` into the closed column.
 */
export default {
    name: 'TicketBoardView',
    components: { FilterBar, StatusCountsWidget, TicketColumn },
    props: {
        tickets: { type: Array, default: () => [] },
        columns: { type: Array, default: () => DEFAULT_COLUMNS },
        filters: {
            type: Object,
            default: () => ({
                query: '',
                channel: 'all',
                category: 'all',
                priority: 'all',
                assignee: 'all',
                statuses: ['new', 'assigned', 'in_progress', 'blocked', 'closed_group'],
            }),
        },
        channelOptions: { type: Array, default: () => [] },
        categoryOptions: { type: Array, default: () => [] },
        priorityOptions: { type: Array, default: () => [] },
        assigneeOptions: { type: Array, default: () => [] },
        statusOptions: {
            type: Array,
            default: () => [
                { value: 'new', label: 'Nuovo' },
                { value: 'assigned', label: 'Assegnato' },
                { value: 'in_progress', label: 'In lavorazione' },
                { value: 'blocked', label: 'Stand-by' },
                { value: 'closed', label: 'Chiusi' },
            ],
        },
        assignees: { type: Array, default: () => [] },
        showAssignment: { type: Boolean, default: false },
        highlightedId: { type: [Number, String], default: null },
        showArchived: { type: Boolean, default: false },
        archivedCount: { type: Number, default: 0 },
        paginationByColumn: { type: Object, default: () => ({}) },
        statusCounts: { type: Array, default: () => [] },
        /** Active desk theme payload (`key` / `label` / `logo_src`) or null. */
        theme: { type: Object, default: null },
        title: { type: String, default: 'Coda ticket' },
        subtitle: { type: String, default: '' },
        currentUserId: { type: [Number, String], default: null },
        showSimulate: { type: Boolean, default: false },
        showClassicToggle: { type: Boolean, default: true },
    },
    emits: [
        'update:filters',
        'update:showArchived',
        'assign',
        'archive',
        'archive-all',
        'restore',
        'page',
        'open',
        'classic-view',
        'simulate',
    ],
    computed: {
        boardColumns() {
            return (this.columns && this.columns.length) ? this.columns : DEFAULT_COLUMNS;
        },
        ticketsByColumn() {
            const buckets = {};
            this.boardColumns.forEach((column) => {
                buckets[column.key] = { active: [], archived: [] };
            });

            (this.tickets || []).forEach((ticket) => {
                const columnKey = this.columnKeyForTicket(ticket);
                if (!buckets[columnKey]) {
                    return;
                }
                if (ticket.archived_at) {
                    buckets[columnKey].archived.push(ticket);
                } else {
                    buckets[columnKey].active.push(ticket);
                }
            });

            this.boardColumns.forEach((column) => {
                if (column.key === 'closed') {
                    return;
                }
                buckets[column.key].active = queueSortTickets(buckets[column.key].active);
            });

            return buckets;
        },
    },
    methods: {
        columnKeyForTicket(ticket) {
            const status = String(ticket.status || '').toLowerCase();
            if (status === 'merged' || status === 'closed') {
                return 'closed';
            }
            if (status === 'blocked') {
                return 'blocked';
            }
            if (status === 'assigned') {
                return 'assigned';
            }
            if (status === 'working' || status === 'in_progress' || status === 'in-progress') {
                return 'in_progress';
            }
            if (status === 'new' || status === 'open') {
                return 'new';
            }
            if (['closed', 'in_progress', 'new', 'assigned', 'blocked'].includes(ticket.column_key)) {
                return ticket.column_key;
            }
            return 'new';
        },
        activeTickets(columnKey) {
            const bucket = this.ticketsByColumn[columnKey];
            return bucket ? bucket.active : [];
        },
        archivedTickets(columnKey) {
            if (columnKey !== 'closed') {
                return [];
            }
            const bucket = this.ticketsByColumn.closed;
            return bucket ? bucket.archived : [];
        },
    },
};
</script>

<template>
    <div class="tickets-index" :data-desk-theme="theme?.key || undefined">
        <header class="tickets-index__header">
            <div class="tickets-index__brand">
                <div class="tickets-index__brand-mark" aria-hidden="true">▤</div>
                <div>
                    <h1 class="tickets-index__title">{{ title }}</h1>
                    <p class="tickets-index__subtitle">{{ subtitle }}</p>
                </div>
            </div>
            <div class="tickets-index__header-extra">
                <StatusCountsWidget :groups="statusCounts" />
                <slot name="header-extra" />
            </div>
        </header>

        <FilterBar
            :filters="filters"
            :channel-options="channelOptions"
            :category-options="categoryOptions"
            :priority-options="priorityOptions"
            :assignee-options="assigneeOptions"
            :status-options="statusOptions"
            :show-classic-toggle="showClassicToggle"
            :show-simulate="showSimulate"
            @update:filters="$emit('update:filters', $event)"
            @classic-view="$emit('classic-view')"
            @simulate="$emit('simulate')"
        >
            <template #actions>
                <slot name="filter-actions" />
            </template>
        </FilterBar>

        <div class="tickets-index__board">
            <div
                class="tickets-index__columns"
                :style="{ '--ti-column-count': String(boardColumns.length || 1) }"
            >
                <TicketColumn
                    v-for="column in boardColumns"
                    :key="column.key"
                    :column-key="column.key"
                    :label="column.label"
                    :tickets="activeTickets(column.key)"
                    :archived-tickets="archivedTickets(column.key)"
                    :archived-count="column.key === 'closed' ? archivedCount : 0"
                    :pagination="paginationByColumn[column.key] || {}"
                    :show-archived="showArchived"
                    :show-assignment="showAssignment"
                    :highlighted-id="highlightedId"
                    :assignees="assignees"
                    :current-user-id="currentUserId"
                    @assign="$emit('assign', $event)"
                    @archive="$emit('archive', $event)"
                    @archive-all="$emit('archive-all', $event)"
                    @restore="$emit('restore', $event)"
                    @page="$emit('page', $event)"
                    @update:show-archived="$emit('update:showArchived', $event)"
                    @open="$emit('open', $event)"
                />
            </div>
        </div>
    </div>
</template>
